import React from 'react';
import classnames from 'classnames';
import styles from '../knowledge.module.scss';
import { KnowledgeCategory } from '../knowledge-category/knowledge-category.component';
import update from 'immutability-helper';
import { useDrop } from 'react-dnd';
import { Lang } from 'shared/constants';
import { CreateBox } from 'src/components/admin';
import { SectionComponent } from 'src/components/common/section/section.component';
import { useSectionOpenState, useViewSelector } from 'src/components/common/view-selector';
import { useSelector } from 'react-redux';
import {
  getCategoryById,
  haveEditPermissions,
  hasExternalSyncDescendantById,
} from 'shared/store/selectors/knowledge.selector';
import { getParentIdByPath } from 'shared/utils/Utils';
import { isExternalSyncReadOnly, isPolicyTechSynced } from 'shared/utils/knowledge.utils';
import { isCollaborator, isRoot } from 'shared/store/selectors/auth.selector';
import { getSelectedWorkspace } from 'shared/store/selectors/workspace.selector';
import { isKnowledgeTreeCopyEnabled } from 'shared/store/selectors/features.selector';
import { KnowledgeCategoryMovePopup } from '../knowledge-category/move-popup/knowledge-category-move';
import { KnowledgeCategoryMirrorPopup } from '../knowledge-category/mirror-popup/knowledge-category-mirror';
import { GridItemsNavigator, ListItemsNavigator } from 'src/components/common/items-navigator';
import { MetadataExtractionModal } from '../metadata-extraction';
import { ConfirmationPopupComponent } from 'src/components/common/confirmation-popup';
import { notification } from 'antd';
import Logger from 'shared/utils/Logger';
import { KnowledgeCategoryDeletePopup } from '../knowledge-category/delete-popup/knowledge-category-delete-popup.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons/faArrowUpRightFromSquare';
import { useMirrorActions } from '../mirror/use-mirror-actions';
import { CategoryMirrorBadge } from '../mirror/category-mirror-badge.component';
import { LocalItemsSeparator } from '../mirror/local-items-separator.component';
import { useMirroredInTargets } from '../mirror/use-mirrored-in-targets';
import { IconAnchor } from 'src/components/design-system/buttons/icon-anchor';

const KnowledgeSectionComponent = React.memo(function KnowledgeSectionComponent({
  id,
  title,
  subtitle,
  children = [],
  adminMode = false,
  activeId = null,
  knowledgeSelectItem = () => {},
  deleteCategory = () => {},
  detachMirroredCategory = () => {},
  editCategory = () => {},
  createCategory = () => {},
  fullPath = null,
  path,
  type,
  updateCategory = () => {},
  lastItem = false,
  firstItem = false,
  changeSectionOrder = () => {},
  updateSubcategoriesOrder = () => {},
  ownerIds = [],
  index = null,
  tags,
  options,
  lang = Lang.ACTION_BUTTON_TOOLTIPS,
  labels,
  mirroredFrom,
  mirroredFromInfo,
  mirroredFromSourceLink,
  mirroredFromWorkspaceId,
}) {
  const { goToSourceAction, sourceHref, mirrorLang, canAccessSourceWorkspace } = useMirrorActions({
    mirroredFromSourceLink,
    mirroredFromWorkspaceId,
  });
  const mirroredInTargets = useMirroredInTargets(id);
  // drag and drop items
  const [isMoveOpen, setMoveOpen] = React.useState(false);
  const [isMirrorOpen, setMirrorOpen] = React.useState(false);
  const [isDeleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);
  const [deleteEntity, setDeleteEntity] = React.useState(null);
  const [isDetachConfirmOpen, setDetachConfirmOpen] = React.useState(false);
  const dndGroup = `contentItems-${id}`;
  const [, drop] = useDrop({ accept: dndGroup });
  // Treat the local-items separator as non-droppable — hovering over it snaps
  // the dragged item back to its starting position, same as the source area.
  const [, separatorDrop] = useDrop({
    accept: dndGroup,
    canDrop: () => false,
    hover({ id: draggedId, originalIndex: draggedOriginalIndex }) {
      dragItem(draggedId, draggedOriginalIndex);
    },
  });
  const [items, setItems] = React.useState(children ?? []);
  let isLoading = false;

  const hasEditPermission = useSelector(state => haveEditPermissions(state, { ownerIds }));

  const isPolicyTechSubtree = useSelector(hasExternalSyncDescendantById([id], isPolicyTechSynced));
  const isReadOnly = isExternalSyncReadOnly(options) || isPolicyTechSubtree;
  const isCreateBlocked = isPolicyTechSubtree;
  const isCollaboratorUser = useSelector(isCollaborator);
  const isRootUser = useSelector(isRoot);
  const isCopyEnabled = useSelector(isKnowledgeTreeCopyEnabled);
  const selectedWorkspaceName = useSelector(getSelectedWorkspace)?.name ?? '';
  const separatorLabel = mirrorLang.LOCAL_ITEMS_SEPARATOR?.replace(
    '{workspace}',
    selectedWorkspaceName,
  );
  const parentId = getParentIdByPath(path);
  const isNestedMirror = useSelector(
    state => !!mirroredFrom && !!getCategoryById(state.knowledge, { id: parentId })?.mirroredFrom,
  );
  const [metadataExtractionOpen, setMetadataExtractionOpen] = React.useState(false);

  React.useEffect(() => {
    setItems(children ?? []);
  }, [children]);

  const dragItem = (id, atIndex) => {
    const { item, index } = findItem(id);
    setItems(
      update(items, {
        $splice: [
          [index, 1],
          [atIndex, 0, item],
        ],
      }),
    );
  };

  const findItem = id => {
    const item = items.find(i => `${i.id}` === id);
    return {
      item,
      index: items.indexOf(item),
    };
  };

  const finishDrag = async categoryId => {
    try {
      await updateSubcategoriesOrder({
        id,
        categoryId,
        index: findItem(categoryId).index,
      });
    } catch (err) {
      notification.error({
        message:
          err?.code === 'CANNOT_REORDER_MIRROR_CHILDREN'
            ? mirrorLang.CANNOT_REORDER_MIRROR_CHILDREN
            : mirrorLang.REORDER_FAILED,
        placement: 'bottomRight',
      });
      // Rollback optimistic reorder
      setItems(children ?? []);
    }
  };

  // Modification actions
  const deleteCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      if (!hasEditPermission) return false;
      if (mirroredFrom) {
        setDeleteConfirmOpen(true);
        return;
      }
      setDeleteEntity({ id, title, type, path, ownerIds });
    },
    [id, title, type, path, ownerIds, hasEditPermission, mirroredFrom],
  );

  const confirmDeleteMirror = React.useCallback(() => {
    deleteCategory({ id });
    setDeleteConfirmOpen(false);
  }, [deleteCategory, id]);

  const detachMirrorAction = React.useCallback(
    e => {
      e.stopPropagation();
      if (!hasEditPermission) return false;
      setDetachConfirmOpen(true);
    },
    [hasEditPermission],
  );

  const confirmDetachMirror = React.useCallback(async () => {
    setDetachConfirmOpen(false);
    try {
      await detachMirroredCategory({ id });
      notification.success({
        message: mirrorLang.DETACH_MIRROR_SUCCESS,
        placement: 'bottomRight',
      });
    } catch (error) {
      Logger.error('Detach mirror failed:', { error });
      notification.error({
        message: mirrorLang.DETACH_MIRROR_ERROR,
        placement: 'bottomRight',
      });
    }
  }, [detachMirroredCategory, id, mirrorLang]);

  const editCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();
      if (!hasEditPermission) {
        return false;
      }

      editCategory({
        id,
        title,
        subtitle,
        ownerIds,
        path,
        type,
        tags,
        options,
        labels,
      });
    },
    [
      hasEditPermission,
      editCategory,
      id,
      title,
      subtitle,
      ownerIds,
      path,
      type,
      tags,
      options,
      labels,
    ],
  );

  const moveCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();

      if (!hasEditPermission) {
        return;
      }

      setMoveOpen(true);
    },
    [hasEditPermission],
  );

  const mirrorCategoryAction = React.useCallback(
    e => {
      e.stopPropagation();

      if (!hasEditPermission) {
        return;
      }

      setMirrorOpen(true);
    },
    [hasEditPermission],
  );

  const extractMetadataAction = React.useCallback(e => {
    e.stopPropagation();
    setMetadataExtractionOpen(true);
  }, []);

  const createNewCategoryAction = React.useCallback(() => {
    // TODO: Analytics
    createCategory({ path: fullPath, ownerIds, tags, labels });
  }, [createCategory, fullPath, ownerIds, tags, labels]);

  const changeOrder = React.useCallback(
    async e => {
      const { target } = e;

      if (isLoading) return false;
      // eslint-disable-next-line react-hooks/exhaustive-deps
      isLoading = true;

      const { dataset } = target;

      const toIndex = dataset.direction === 'up' ? index - 1 : index + 1;
      await changeSectionOrder(id, toIndex);
      isLoading = false;
    },
    [index, id],
  );

  const { isGridView } = useViewSelector();

  const renderItems = () => {
    if (!items) return null;

    // Boundary between source-derived (above) and user-added (below) in a mirror section.
    const separatorIndex = mirroredFrom ? items.findIndex(item => !item.mirroredFrom) : -1;
    const showSeparator = separatorIndex > 0 && separatorIndex < items.length;

    const categoryItems = items.flatMap((item, index) => {
      const node = (
        <KnowledgeCategory
          key={`content-box-${item.id}`}
          {...item}
          {...{
            adminMode,
            activeId,
            knowledgeSelectItem,
            deleteCategory,
            detachMirroredCategory,
            updateCategory,
            editCategory,
            dragItem,
            findItem,
            dndGroup,
            finishDrag,
            lang,
          }}
        />
      );
      if (showSeparator && index === separatorIndex) {
        return [
          <LocalItemsSeparator
            key="local-items-separator"
            label={separatorLabel}
            dropRef={separatorDrop}
          />,
          node,
        ];
      }
      return [node];
    });

    if (isGridView) {
      return (
        <div ref={drop}>
          <GridItemsNavigator className={styles.items} dynamicColumns>
            {categoryItems}
            <CreateBox
              adminMode={adminMode}
              disabled={isCreateBlocked}
              disabledTooltip={lang.READ_ONLY_SYNCED}
              onClick={createNewCategoryAction}
              lang={lang}
            />
          </GridItemsNavigator>
        </div>
      );
    }

    return (
      <div ref={drop} className={styles.dropContainer}>
        <ListItemsNavigator className={styles.itemsColumn}>
          {categoryItems}
          <CreateBox
            adminMode={adminMode}
            disabled={isCreateBlocked}
            disabledTooltip={lang.READ_ONLY_SYNCED}
            onClick={createNewCategoryAction}
            lang={lang}
          />
        </ListItemsNavigator>
      </div>
    );
  };

  const actionDisabledOnMirrorTooltip =
    mirrorLang[
      `ACTION_DISABLED_FOR_MIRRORED_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`
    ];
  const cannotMirrorAMirrorTooltip =
    mirrorLang[
      `MIRROR_ACTION_DISABLED_FOR_MIRRORED_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`
    ];

  const adminActions = [
    {
      label: lang.MOVE_UP,
      onClick: changeOrder,
      tooltip: isReadOnly
        ? lang.READ_ONLY_SYNCED
        : mirroredFrom
          ? actionDisabledOnMirrorTooltip
          : undefined,
      disabled: !!mirroredFrom || isReadOnly,
      props: { 'data-direction': 'up', hidden: firstItem },
    },
    {
      label: lang.MOVE_DOWN,
      onClick: changeOrder,
      tooltip: isReadOnly
        ? lang.READ_ONLY_SYNCED
        : mirroredFrom
          ? actionDisabledOnMirrorTooltip
          : undefined,
      disabled: !!mirroredFrom || isReadOnly,
      props: { 'data-direction': 'down', hidden: lastItem },
    },
    {
      label: lang.EDIT,
      onClick: editCategoryAction,
      tooltip: mirroredFrom
        ? actionDisabledOnMirrorTooltip
        : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom,
    },
    {
      label: lang.MOVE,
      onClick: moveCategoryAction,
      tooltip: isReadOnly
        ? lang.READ_ONLY_SYNCED
        : mirroredFrom
          ? actionDisabledOnMirrorTooltip
          : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom || isReadOnly,
    },
    {
      label: lang.MIRROR,
      onClick: mirrorCategoryAction,
      tooltip: mirroredFrom
        ? cannotMirrorAMirrorTooltip
        : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      disabled: !hasEditPermission || !!mirroredFrom,
      props: { hidden: !isCollaboratorUser || !hasEditPermission || !isCopyEnabled },
    },
    {
      label: lang.EXTRACT_METADATA,
      onClick: extractMetadataAction,
      tooltip: mirroredFrom && actionDisabledOnMirrorTooltip,
      disabled: !!mirroredFrom,
      props: { hidden: !isRootUser },
    },
    {
      label: lang.DELETE,
      onClick: deleteCategoryAction,
      tooltip:
        isReadOnly && !mirroredFrom
          ? lang.READ_ONLY_SYNCED
          : !hasEditPermission && lang.NOT_ALLOWED_TO_EDIT,
      // Deleting a mirror removes only the mirror doc, never the synced source.
      disabled: !hasEditPermission || (isReadOnly && !mirroredFrom),
    },
    {
      label: lang.DETACH_MIRROR,
      onClick: detachMirrorAction,
      tooltip: !hasEditPermission && lang.NOT_ALLOWED_TO_DETACH,
      disabled: !hasEditPermission,
      props: { hidden: !mirroredFrom || isNestedMirror },
    },
  ];

  const onBlur = React.useCallback(
    e => {
      e.stopPropagation();
      const { textContent: value } = e.target;
      // TODO: add analytics

      if (value === title) return false;

      updateCategory({ id, title: value, tags });
    },
    [id, tags, title, updateCategory],
  );

  const { isOpen, onToggle } = useSectionOpenState({
    id,
    isFirst: firstItem,
    isContainsActiveBinder: items.some(item => item.id === activeId),
  });

  return (
    <section
      key={id}
      className={classnames(styles.section, adminMode ? styles.admin : {})}
      role="directory"
      aria-label={title}>
      <span className={styles.anchor} data-type="anchor" id={id} />
      <SectionComponent
        title={title}
        showActions={adminMode}
        onBlur={onBlur}
        adminActions={adminActions}
        isOpen={isOpen}
        onToggle={onToggle}
        customActions={
          adminMode && isCollaboratorUser && mirroredFrom && canAccessSourceWorkspace ? (
            <IconAnchor
              label={
                mirrorLang[`GO_TO_SOURCE_${mirroredFromInfo?.sourceCategoryType?.toUpperCase()}`]
              }
              href={sourceHref}
              onClick={e => {
                e.stopPropagation();
                if (e.metaKey || e.ctrlKey || e.shiftKey) return;
                e.preventDefault();
                goToSourceAction();
              }}
              className={styles.goToSourceButton}>
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            </IconAnchor>
          ) : null
        }
        badge={
          <CategoryMirrorBadge
            adminMode={adminMode}
            isCollaboratorUser={isCollaboratorUser}
            mirroredFrom={mirroredFrom}
            mirroredFromInfo={mirroredFromInfo}
            mirroredInTargets={mirroredInTargets}
            mirrorLang={mirrorLang}
            className={styles.mirrorBadge}
            iconClassName={styles.mirrorBadgeIcon}
          />
        }>
        {renderItems()}
      </SectionComponent>
      <KnowledgeCategoryMovePopup
        isOpen={isMoveOpen}
        categoryId={id}
        id={id}
        onFinish={() => setMoveOpen(false)}
      />
      <KnowledgeCategoryMirrorPopup
        isOpen={isMirrorOpen}
        id={id}
        onFinish={() => setMirrorOpen(false)}
      />
      <MetadataExtractionModal
        open={metadataExtractionOpen}
        onClose={() => setMetadataExtractionOpen(false)}
        categoryId={id}
        categoryTitle={title}
      />
      <ConfirmationPopupComponent
        open={isDeleteConfirmOpen}
        title={mirrorLang.DELETE_MIRROR_TITLE}
        message={mirrorLang.DELETE_MIRROR_MESSAGE}
        onConfirm={confirmDeleteMirror}
        onDismiss={() => setDeleteConfirmOpen(false)}
      />
      <ConfirmationPopupComponent
        open={isDetachConfirmOpen}
        title={mirrorLang.DETACH_MIRROR_TITLE}
        message={mirrorLang.DETACH_MIRROR_MESSAGE}
        onConfirm={confirmDetachMirror}
        onDismiss={() => setDetachConfirmOpen(false)}
      />
      <KnowledgeCategoryDeletePopup
        isOpen={!!deleteEntity}
        entity={deleteEntity}
        deleteCategory={deleteCategory}
        onFinish={() => setDeleteEntity(null)}
      />
    </section>
  );
});

export { KnowledgeSectionComponent };
